import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { API_BASE_PATH, API_ERROR_CODE, type ApiError } from '@mirsad/shared';

/**
 * Client HTTP unique.
 *
 * `withCredentials` est indispensable : les jetons vivent dans des cookies
 * HttpOnly, jamais dans le stockage local — un script injecté ne peut donc
 * pas les lire.
 */
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || API_BASE_PATH,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

/** Routes dont un 401 ne doit surtout pas déclencher de renouvellement. */
const SANS_RENOUVELLEMENT = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/mfa/verify'];

/**
 * Une seule tentative de renouvellement à la fois.
 *
 * Sans cette promesse partagée, dix requêtes simultanées recevant 401
 * déclencheraient dix appels à /auth/refresh : la rotation des jetons en
 * invaliderait neuf, et l'utilisateur serait déconnecté sans raison.
 */
let renouvellementEnCours: Promise<void> | null = null;

function renouveler(): Promise<void> {
  renouvellementEnCours ??= api
    .post('/auth/refresh')
    .then(() => undefined)
    .finally(() => {
      renouvellementEnCours = null;
    });
  return renouvellementEnCours;
}

function versConnexion(): void {
  const courant = window.location.pathname + window.location.search;
  if (window.location.pathname.startsWith('/connexion')) return;
  const suite = encodeURIComponent(courant);
  window.location.assign(`/connexion?suite=${suite}`);
}

api.interceptors.response.use(
  (reponse) => reponse,
  async (erreur: AxiosError<ApiError>) => {
    const requete = erreur.config as (AxiosRequestConfig & { _rejoue?: boolean }) | undefined;
    const statut = erreur.response?.status;
    const code = erreur.response?.data?.code;
    const url = requete?.url ?? '';

    // Mot de passe à changer : l'API ferme les routes métier, on redirige.
    if (statut === 403 && code === API_ERROR_CODE.PASSWORD_CHANGE_REQUIRED) {
      if (!window.location.pathname.startsWith('/changer-mot-de-passe')) {
        window.location.assign('/changer-mot-de-passe');
      }
      return Promise.reject(erreur);
    }

    const renouvelable =
      statut === 401 &&
      requete &&
      !requete._rejoue &&
      !SANS_RENOUVELLEMENT.some((r) => url.includes(r));

    if (renouvelable) {
      requete._rejoue = true;
      try {
        await renouveler();
        return api.request(requete);
      } catch {
        versConnexion();
        return Promise.reject(erreur);
      }
    }

    if (statut === 401 && !SANS_RENOUVELLEMENT.some((r) => url.includes(r))) {
      versConnexion();
    }

    return Promise.reject(erreur);
  },
);

/** Extrait un message lisible d'une erreur, sans jamais exposer d'interne. */
export function messageErreur(erreur: unknown, defaut = 'Une erreur est survenue.'): string {
  if (erreur instanceof AxiosError) {
    return erreur.response?.data?.message ?? erreur.message ?? defaut;
  }
  if (erreur instanceof Error) return erreur.message;
  return defaut;
}

/** Code applicatif d'une erreur, s'il est présent. */
export function codeErreur(erreur: unknown): string | undefined {
  return erreur instanceof AxiosError ? erreur.response?.data?.code : undefined;
}
